import React, { useState } from 'react';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProdottiTab = ({ products }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [imageStates, setImageStates] = useState({});

  const categories = ['all', ...new Set(products.map(p => p.category))];

  const filteredProducts = selectedCategory === 'all'
    ? products
    : products?.filter(p => p?.category === selectedCategory);

  const handleImageLoad = (productId) => {
    setImageStates(prev => ({ ...prev, [productId]: 'loaded' }));
  };

  const handleImageError = (productId) => {
    setImageStates(prev => ({ ...prev, [productId]: 'error' }));
  };

  return (
    <div className="space-y-6 md:space-y-8">
      <div className="flex items-center gap-2 overflow-x-auto scrollbar-hide pb-2">
        {categories?.map((category) => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 md:px-6 py-2 md:py-3 rounded-lg font-caption font-semibold text-sm md:text-base transition-smooth whitespace-nowrap flex-shrink-0 ${
              selectedCategory === category
                ? 'bg-primary text-primary-foreground shadow-warm'
                : 'bg-card text-muted-foreground hover:text-foreground hover:bg-muted'
            }`}
          >
            {category === 'all' ? 'Tutti i Prodotti' : category}
          </button>
        ))}
      </div>
      {filteredProducts?.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {filteredProducts?.map((product) => (
            <div
              key={product?.id}
              className="bg-card rounded-lg md:rounded-xl overflow-hidden shadow-warm group"
            >
              <div className="relative h-48 md:h-56 lg:h-64 overflow-hidden bg-muted">
                {/* Skeleton Loader */}
                {!imageStates?.[product?.id] && (
                  <div className="absolute inset-0 bg-gradient-to-r from-muted via-muted-foreground/10 to-muted animate-pulse" />
                )}

                {/* Error Fallback */}
                {imageStates?.[product?.id] === 'error' ? (
                  <div className="absolute inset-0 flex items-center justify-center bg-muted">
                    <Icon name="ImageOff" size={48} className="text-muted-foreground" />
                  </div>
                ) : (
                  <Image
                    src={product?.image}
                    alt={product?.imageAlt || `Immagine prodotto ${product?.name}`}
                    className={`w-full h-full object-cover transition-smooth group-hover:scale-110 ${
                      imageStates?.[product?.id] === 'loaded' ? 'opacity-100' : 'opacity-0'
                    }`}
                    loading="lazy"
                    decoding="async"
                    onLoad={() => handleImageLoad(product?.id)}
                    onError={() => handleImageError(product?.id)}
                    style={{ width: '100%', height: '100%' }}
                  />
                )}

                {product?.isNew && (
                  <div className="absolute top-3 left-3 px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-xs font-caption font-semibold">
                    Nuovo
                  </div>
                )}
                {product?.discount && (
                  <div className="absolute top-3 right-3 px-3 py-1.5 rounded-full bg-destructive text-destructive-foreground text-xs font-caption font-semibold">
                    -{product?.discount}%
                  </div>
                )}
              </div>

              <div className="p-4 md:p-6">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <h3 className="text-base md:text-lg font-heading font-bold text-foreground line-clamp-2 flex-1">
                    {product?.name}
                  </h3>
                  {product?.inStock ? (
                    <span className="flex items-center gap-1 text-xs text-success whitespace-nowrap">
                      <Icon name="Check" size={14} />
                      Disponibile
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-xs text-destructive whitespace-nowrap">
                      <Icon name="X" size={14} />
                      Esaurito
                    </span>
                  )}
                </div>

                <p className="text-sm text-muted-foreground mb-3 line-clamp-2">
                  {product?.description}
                </p>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-baseline gap-2">
                    <span className="text-xl md:text-2xl font-heading font-bold text-primary">
                      €{product?.price?.toFixed(2)}
                    </span>
                    {product?.originalPrice && (
                      <span className="text-sm text-muted-foreground line-through">
                        €{product?.originalPrice?.toFixed(2)}
                      </span>
                    )}
                  </div>
                </div>

                <Button
                  variant="default"
                  size="default"
                  iconName="ShoppingCart"
                  iconPosition="left"
                  disabled={!product?.inStock}
                  className="w-full"
                >
                  {product?.inStock ? 'Aggiungi al Carrello' : 'Non Disponibile'}
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-card rounded-lg md:rounded-xl lg:rounded-2xl p-8 md:p-12 text-center shadow-warm">
          <div className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-muted mx-auto mb-4 flex items-center justify-center">
            <Icon name="Package" size={32} className="text-muted-foreground" />
          </div>
          <h3 className="text-lg md:text-xl font-heading font-bold text-foreground mb-2">
            Nessun Prodotto Disponibile
          </h3>
          <p className="text-sm md:text-base text-muted-foreground">
            Non ci sono prodotti in questa categoria al momento.
          </p>
        </div>
      )}
    </div>
  );
};

export default ProdottiTab;